import { createAppAsyncThunk } from '@/redux/create-app-thunk';
import { validateCurrentCheckoutStep } from '@/redux/slices/checkout.slice';
import { checkoutRedirectUrl, homeRedirectUrl } from '@/redux/thunks/checkout/index';
import { AppDispatch } from '@/redux';
import { valueof } from '@/types';
import { validateAddressStep } from '@/redux/thunks/checkout/validateAddressStep';
import { isObjectEmpty } from '@/utils/comparisonUtils';
import { STEPS } from '@/constants/checkout';

const redirectTo = async (dispatch: AppDispatch, step: valueof<typeof STEPS>, url: string) => {
  if (url === '') {
    dispatch(validateCurrentCheckoutStep('report'));
    return await homeRedirectUrl('');
  }
  dispatch(validateCurrentCheckoutStep(step));
  return await checkoutRedirectUrl(url);
};

const rejectWithStep = (step: valueof<typeof STEPS>, rejectWithValue: (value: { previousValidatedStep: string }) => void) => {
  return rejectWithValue({ previousValidatedStep: step });
};

const handleUserNotConnected = async (
  dispatch: AppDispatch,
  rejectWithValue: (value: { previousValidatedStep: string }) => void,
  currentStep: valueof<typeof STEPS>
) => {
  dispatch(validateCurrentCheckoutStep('login'));
  await checkoutRedirectUrl('/login');
  return rejectWithStep(currentStep, rejectWithValue);
};

type ValidateOrRedirectPayload = {
  type: 'redirectOnly' | 'validateAndRedirect' | 'validateOnly',
  checkAddressForRedirect?: boolean
}

export const validateCurrentCheckoutStepOrRedirect = createAppAsyncThunk(
  'validateCheckoutCurrentStepOrRedirect',
  async (payload: ValidateOrRedirectPayload, {
    getState, dispatch, rejectWithValue, extra: { userService }
  }) => {
    const step = getState().checkout.step.label;
    const isUserConnected = !isObjectEmpty(getState().user.content) || !isObjectEmpty(getState().auth.loggedUser);
    const userCart = getState().cart.content;
    const cartStorage: Record<string, any> = getState().storage.cartStorage;

    const redirectOnly = payload.type === 'redirectOnly';
    const validateOnly = payload.type === 'validateOnly';

    if (!step) return;
    if (userCart?.products?.length === 0 && cartStorage?.length === 0 && step !== 'report' && step !== 'cart') {
      return await redirectTo(dispatch, 'cart', '/cart');
    }
    if (validateOnly) return;

    switch (step) {
      case 'cart': {
        if (redirectOnly) return;
        if (!isUserConnected) {
          return await handleUserNotConnected(dispatch, rejectWithValue, 'cart');
        }
        return await redirectTo(dispatch, 'address', '/address');
      }

      case 'login': {
        if (!isUserConnected) {
          return await handleUserNotConnected(dispatch, rejectWithValue, 'cart');
        }
        return await redirectTo(dispatch, 'address', '/address');
      }

      case 'address': {
        if (!isUserConnected) {
          return await handleUserNotConnected(dispatch, rejectWithValue, 'cart');
        }
        if (redirectOnly) return;
        if (payload?.checkAddressForRedirect) {
          const shouldContinue = await validateAddressStep(getState(), dispatch, userService);
          if (!shouldContinue?.continue) {
            return;
          }
        }
        if (userCart.billing?.idNext !== userCart.shipping?.idNext || isObjectEmpty(userCart.billing)) {
          return await redirectTo(dispatch, 'billing', '/billing');
        }
        return await redirectTo(dispatch, 'shipping', '/shipping');
      }

      case 'billing': {
        if (!isUserConnected) {
          return await handleUserNotConnected(dispatch, rejectWithValue, 'cart');
        }
        if (!userCart.billing) return;
        if (redirectOnly) {
          if (!userCart.shipping || isObjectEmpty(userCart.shipping)) {
            return await redirectTo(dispatch, 'address', '/address');
          }
          return;
        }
        return await redirectTo(dispatch, 'shipping', '/shipping');
      }

      case 'shipping': {
        if (redirectOnly) {
          if (isObjectEmpty(userCart.shipping)) {
            return await redirectTo(dispatch, 'address', '/address');
          }
          if (isObjectEmpty(userCart.billing)) {
            return await redirectTo(dispatch, 'billing', '/billing');
          }
          return;
        }
        if (!isUserConnected) {
          return await handleUserNotConnected(dispatch, rejectWithValue, 'cart');
        }

        if ((!userCart.shippingMode || isObjectEmpty(userCart.shippingMode)) &&
          (!userCart.primeurShippingMode || isObjectEmpty(userCart.primeurShippingMode)) &&
          (!userCart.prearrivalShippingMode || isObjectEmpty(userCart.prearrivalShippingMode))) {
          return;
        }

        return await redirectTo(dispatch, 'review', '/review');
      }

      case 'review': {
        if (!isUserConnected) {
          return await handleUserNotConnected(dispatch, rejectWithValue, 'cart');
        }
        if (userCart.products?.length === 0 && cartStorage?.length === 0) {
          return await redirectTo(dispatch, 'cart', '/cart');
        }
        if (!userCart.shipping?.idNext) {
          return await redirectTo(dispatch, 'address', '/address');
        }
        if (!userCart.billing?.idNext) {
          return await redirectTo(dispatch, 'billing', '/billing');
        }
        if (!userCart.shippingMode && !userCart.primeurShippingMode && !userCart.prearrivalShippingMode) {
          return await redirectTo(dispatch, 'shipping', '/shipping');
        }
        if (redirectOnly) return;
        return await redirectTo(dispatch, 'pay', '/pay');
      }

      case 'pay': {
        if (!isUserConnected) {
          return await handleUserNotConnected(dispatch, rejectWithValue, 'cart');
        }
        if (!userCart.shipping?.idNext) {
          return await redirectTo(dispatch, 'address', '/address');
        }
        if (!userCart.billing?.idNext) {
          return await redirectTo(dispatch, 'billing', '/billing');
        }
        if (!userCart.shippingMode && !userCart.primeurShippingMode && !userCart.prearrivalShippingMode) {
          return await redirectTo(dispatch, 'shipping', '/shipping');
        }
        if (redirectOnly) return { processPayment: false };

        return { processPayment: true };
      }

      case 'report': {
        if (redirectOnly) return;
        return await redirectTo(dispatch, 'report', '');
      }

      default:
        return;
    }
  }
);
